// src/components/Layout/MobileHeader/index.tsx
import { FC, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Drawer, Menu, Button, App } from 'antd';
import type { MenuProps } from 'antd';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { PATHS } from '@/router/paths';
import useThemeStore from '@/store/useThemeStore';
import { toggleThemeWithSync } from '@/utils/themeSync';
import { setLanguage } from '@/utils/languageSync';
import SvgIcon from '@/components/SvgIcon';
import styles from './index.module.scss';

// 导入国旗图标
import EnglishLight from '@/assets/svg/menu/LIGHT/English.svg';
import EnglishDark from '@/assets/svg/menu/DARK/English.svg';
import FrenchLight from '@/assets/svg/menu/LIGHT/French.svg';
import FrenchDark from '@/assets/svg/menu/DARK/French.svg';

type MenuItem = Required<MenuProps>['items'][number];
type Language = 'en' | 'fr';

const normalizeLanguage = (lang: string): Language => {
  const code = lang.split('-')[0];
  return code === 'fr' ? 'fr' : 'en';
};

const IconFont: FC<{ type: string; className?: string }> = ({ type, className }) => (
  <svg className={`${styles.iconFont} ${className || ''}`} aria-hidden="true">
    <use xlinkHref={`#icon-${type}`} />
  </svg>
);

const MENU_ICON_MAP = {
  dashboard: 'dashboard',
  documentAnalysis: 'Document-Analysis',
  documentGeneration: 'Document-Generation',
  interviewSimulation: 'Interview-Simulation',
} as const;

const BOTTOM_ICON_MAP = {
  profile: 'profile',
  subscription: 'Subscription',
  contactUs: 'Contact-Us',
} as const;

const MobileHeader: FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [isTogglingTheme, setIsTogglingTheme] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const { isDarkMode } = useThemeStore();
  const currentLanguage = normalizeLanguage(i18n.language || 'en');

  const logoSrc = isDarkMode ? '/logo/THEPATCH-D.png' : '/logo/THEPATCH-L.png';
  const logoSrcSet = isDarkMode ? '/logo/THEPATCH-D.png 1x, /logo/THEPATCH-D@2x.png 2x' : '/logo/THEPATCH-L.png 1x, /logo/THEPATCH-L@2x.png 2x';

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  const activeIconPath = isDarkMode ? 'menu-DARK' : 'menu-LIGHT';

  const menuItems: MenuItem[] = [
    {
      key: PATHS.dashboard,
      icon: isActive(PATHS.dashboard) ? <SvgIcon name={MENU_ICON_MAP.dashboard} path={activeIconPath} size={20} className={styles.iconFontGradient} /> : <IconFont type="dashboard" />,
      label: t('menu.dashboard'),
    },
    {
      key: PATHS.documentAnalysis.root,
      icon: isActive(PATHS.documentAnalysis.root) ? (
        <SvgIcon name={MENU_ICON_MAP.documentAnalysis} path={activeIconPath} size={20} className={styles.iconFontGradient} />
      ) : (
        <IconFont type="a-DocumentAnalysis" />
      ),
      label: t('menu.documentAnalysis'),
      children: [
        { key: PATHS.documentAnalysis.cv, label: t('menu.cv') },
        { key: PATHS.documentAnalysis.coverLetter, label: t('menu.coverLetter') },
        { key: PATHS.documentAnalysis.history, label: t('menu.history') },
      ],
    },
    {
      key: PATHS.documentGeneration.root,
      icon: isActive(PATHS.documentGeneration.root) ? (
        <SvgIcon name={MENU_ICON_MAP.documentGeneration} path={activeIconPath} size={20} className={styles.iconFontGradient} />
      ) : (
        <IconFont type="a-DocumentGeneration" />
      ),
      label: t('menu.documentGeneration'),
      children: [
        { key: PATHS.documentGeneration.coverLetter, label: t('menu.coverLetter') },
        { key: PATHS.documentGeneration.history, label: t('menu.history') },
      ],
    },
    {
      key: PATHS.interviewSimulation.root,
      icon: isActive(PATHS.interviewSimulation.root) ? (
        <SvgIcon name={MENU_ICON_MAP.interviewSimulation} path={activeIconPath} size={20} className={styles.iconFontGradient} />
      ) : (
        <IconFont type="a-InterviewSimulation" />
      ),
      label: t('menu.interviewSimulation'),
      children: [
        { key: PATHS.interviewSimulation.start, label: t('menu.startInterview') },
        { key: PATHS.interviewSimulation.history, label: t('menu.interviewHistory') },
      ],
    },
  ];

  // 语言切换项使用国旗图标
  const languageIconName = currentLanguage === 'en' ? 'English' : 'French';
  const languageLabel = currentLanguage === 'en' ? 'English' : 'Français';

  const iconMap = {
    English: { light: EnglishLight, dark: EnglishDark },
    French: { light: FrenchLight, dark: FrenchDark },
  };

  const languageIconSrc = isDarkMode ? iconMap[languageIconName as 'English' | 'French'].dark : iconMap[languageIconName as 'English' | 'French'].light;

  const bottomItems: MenuItem[] = [
    {
      key: 'language',
      icon: <img src={languageIconSrc} alt={languageLabel} className={styles.languageFlag} style={{ width: '20px', height: '20px' }} />,
      label: languageLabel,
    },
    {
      key: PATHS.subscription,
      icon: isActive(PATHS.subscription) ? <SvgIcon name={BOTTOM_ICON_MAP.subscription} path={activeIconPath} size={20} className={styles.iconFontGradient} /> : <IconFont type="Subscription" />,
      label: t('menu.subscription'),
    },
    {
      key: PATHS.contactUs,
      icon: isActive(PATHS.contactUs) ? <SvgIcon name={BOTTOM_ICON_MAP.contactUs} path={activeIconPath} size={20} className={styles.iconFontGradient} /> : <IconFont type="a-ContactUs" />,
      label: t('menu.contactUs'),
    },
    {
      key: PATHS.profile,
      icon: isActive(PATHS.profile) ? <SvgIcon name={BOTTOM_ICON_MAP.profile} path={activeIconPath} size={20} className={styles.iconFontGradient} /> : <IconFont type="profile" />,
      label: t('menu.profile'),
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key.startsWith('/')) {
      navigate(key);
      setDrawerOpen(false);
    }
  };

  const handleBottomMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'language') {
      setLanguage(currentLanguage === 'en' ? 'fr' : 'en');
    } else if (key.startsWith('/')) {
      navigate(key);
      setDrawerOpen(false);
    }
  };

  const handleToggleTheme = async () => {
    if (isTogglingTheme) return;
    setIsTogglingTheme(true);
    const success = await toggleThemeWithSync();
    setIsTogglingTheme(false);
    if (!success) {
      message.error('Failed to sync theme preference');
    }
  };

  const handleOpenChange: MenuProps['onOpenChange'] = (keys) => {
    const nextKeys = keys as string[];

    // 手风琴逻辑：只允许一个子菜单展开
    const latestOpenKey = nextKeys.find((key) => !openKeys.includes(key));
    if (latestOpenKey) {
      // 有新打开的子菜单，只保留这一个
      setOpenKeys([latestOpenKey]);
    } else {
      // 没有新打开的，说明是关闭操作
      setOpenKeys(nextKeys);
    }
  };

  return (
    <>
      <header className={styles.mobileHeader}>
        <div className={styles.logoWrapper}>
          <img src={logoSrc} srcSet={logoSrcSet} alt="The Patch" className={styles.logo} />
        </div>
        <div className={styles.headerRight}>
          <Button className={styles.themeBtn} type="text" icon={<IconFont type={isDarkMode ? 'a-lightmode1' : 'dark_mode1'} />} onClick={handleToggleTheme} loading={isTogglingTheme} />
          <button className={styles.menuBtn} onClick={() => setDrawerOpen(true)} aria-label="Open menu">
            <MenuOutlined style={{ fontSize: 22 }} />
          </button>
        </div>
      </header>

      <Drawer
        placement="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        className={styles.drawer}
        styles={{
          header: { display: 'none' },
          body: { padding: 0, display: 'flex', flexDirection: 'column' },
          wrapper: { width: '100%' },
        }}
      >
        <div className={styles.drawerHeader}>
          <img src={logoSrc} srcSet={logoSrcSet} alt="The Patch" className={styles.drawerLogo} />
          <button className={styles.closeBtn} onClick={() => setDrawerOpen(false)} aria-label="Close menu">
            <CloseOutlined style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className={styles.drawerContent}>
          <Menu className={styles.drawerMenu} mode="inline" selectedKeys={[location.pathname]} openKeys={openKeys} onOpenChange={handleOpenChange} items={menuItems} onClick={handleMenuClick} />

          <Menu className={styles.drawerBottomMenu} mode="inline" selectable={false} items={bottomItems} onClick={handleBottomMenuClick} />
        </div>
      </Drawer>
    </>
  );
};

export default MobileHeader;
